import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../lib/api';
import { toast } from 'sonner';
import { Home, Trophy, Users, User, LogOut, LayoutDashboard, BarChart3, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { NotificationBell } from '../components/NotificationBell';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage, isAlreadyMember } from '../lib/apiErrors';

// Logo URL
const LOGO_URL = "/logo.jpeg";

export function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Complete a pending group-invite join (set by /groups/join/:code before signup)
  useEffect(() => {
    if (!user) return;
    let code = null;
    try { code = localStorage.getItem('pending_group_code'); } catch {}
    if (!code) return;
    try { localStorage.removeItem('pending_group_code'); } catch {}
    userAPI.joinGroup(code)
      .then(() => { toast.success(t('groups.joinedToast', 'Joined the group! 🎉')); navigate('/groups'); })
      .catch((err) => {
        if (isAlreadyMember(err)) toast.info(t('groups.alreadyMember', 'You are already in this group'));
        else toast.error(apiErrorMessage(err, t, 'groups.joinCheckCode'));
      });
  }, [user, navigate, t]);

  const handleLogout = () => {
    logout();
    navigate('/welcome');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0a1628] text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-teal-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="bg-[#0a1628]/95 backdrop-blur-lg border-b border-slate-700/50 sticky top-0 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/home" className="flex items-center space-x-3 group">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative"
              >
                <div className="w-10 h-10 rounded-full shadow-lg shadow-teal-500/30 overflow-hidden border-2 border-teal-400/30">
                  <img src={LOGO_URL} alt="SocraQuest" className="w-full h-full object-cover" />
                </div>
              </motion.div>
              <span className="text-xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                SocraQuest
              </span>
            </Link>
            
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              {user && <NotificationBell />}

              {user && (
                <>
                  <div className="hidden sm:flex items-center space-x-3 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700/50">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm">
                      {user.nickname?.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-slate-300">{user.nickname}</span>
                  </div>
                  <Button 
                    onClick={handleLogout} 
                    variant="ghost" 
                    size="sm" 
                    className="text-slate-400 hover:text-teal-400 hover:bg-slate-800"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:ml-64 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      </main>

      {/* Bottom Navigation (Mobile) - Fixed with safe area padding */}
      {user && (
        <motion.nav 
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 bg-[#0d1f35]/95 backdrop-blur-lg border-t border-slate-700/50 z-50 md:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="flex justify-around items-center h-16 pb-safe">
            {[
              { to: '/dashboard', icon: BarChart3, label: t('nav.insights'), testId: 'nav-dashboard' },
              { to: '/home', icon: Home, label: t('nav.home'), testId: 'nav-home' },
              { to: '/rankings', icon: Trophy, label: t('nav.rankings'), testId: 'nav-rankings' },
              { to: '/groups', icon: Users, label: t('nav.groups'), testId: 'nav-groups' },
              { to: '/profile', icon: User, label: t('nav.profile'), testId: 'nav-profile' },
              ...(isAdmin ? [{ to: '/admin', icon: LayoutDashboard, label: t('nav.admin'), testId: 'nav-admin' }] : [])
            ].map(({ to, icon: Icon, label, testId }) => (
              <Link
                key={to}
                to={to}
                className={`flex flex-col items-center justify-center flex-1 py-3 transition-all ${
                  isActive(to) || (to === '/admin' && location.pathname.startsWith('/admin'))
                    ? 'text-teal-400' 
                    : 'text-slate-500 hover:text-teal-400'
                }`}
                data-testid={testId}
              >
                <Icon className="w-6 h-6" />
                <span className="text-xs mt-1 font-medium">{label}</span>
              </Link>
            ))}
          </div>
        </motion.nav>
      )}

      {/* Desktop Navigation (Sidebar) */}
      {user && (
        <motion.nav 
          initial={{ x: -300 }}
          animate={{ x: 0 }}
          className="hidden md:block fixed left-0 top-16 bottom-0 w-64 bg-[#0d1f35]/80 backdrop-blur-lg border-r border-slate-700/50 z-40"
        >
          <div className="p-6 space-y-2">
            {[
              { to: '/dashboard', icon: BarChart3, label: t('nav.insights'), testId: 'desktop-nav-dashboard' },
              { to: '/home', icon: Home, label: t('nav.home'), testId: 'desktop-nav-home' },
              { to: '/learning', icon: BookOpen, label: t('nav.learning'), testId: 'desktop-nav-learning' },
              { to: '/rankings', icon: Trophy, label: t('nav.rankings'), testId: 'desktop-nav-rankings' },
              { to: '/groups', icon: Users, label: t('nav.groups'), testId: 'desktop-nav-groups' },
              { to: '/profile', icon: User, label: t('nav.profile'), testId: 'desktop-nav-profile' },
              ...(isAdmin ? [{ to: '/admin', icon: LayoutDashboard, label: t('nav.admin'), testId: 'desktop-nav-admin' }] : [])
            ].map(({ to, icon: Icon, label, testId }) => (
              <Link key={to} to={to} data-testid={testId}>
                <motion.div
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    isActive(to) || (to === '/admin' && location.pathname.startsWith('/admin'))
                      ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30' 
                      : 'text-slate-400 hover:bg-slate-800/50 hover:text-teal-400'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{label}</span>
                </motion.div>
              </Link>
            ))}
          </div>
        </motion.nav>
      )}
      
      {/* Padding for bottom nav on mobile - with safe area */}
      {user && <div className="h-20 md:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} />}
    </div>
  );
}
